import { useEnvironmentInterface } from "environment-interface";
import type { CsbApi } from "environment-interface/csbApi";
import type { Reducer } from "react";
import { useCallback, useReducer } from "react";

type AwaitedReturnType<F extends (...args: unknown[]) => Promise<unknown>> =
  Awaited<ReturnType<F>>;

export type UseLegacyCsbApiState<InvokeFn extends UseCsbApiInvokeFn> =
  | {
      // Default state.
      state: "idle";
      input: null;
      error: null;
      data: null;
    }
  | {
      // Loading state.
      state: "loading";
      input: Parameters<InvokeFn>;
      error: null;
      data: null;
    }
  | {
      // Error state.
      state: "done";
      input: Parameters<InvokeFn>;
      error: Error;
      data: null;
    }
  | {
      // Success state.
      state: "done";
      input: Parameters<InvokeFn>;
      error: null;
      data: AwaitedReturnType<InvokeFn>;
    };

type UseCsbApiAction<InvokeFn extends UseCsbApiInvokeFn> =
  | { type: "CSB_API_CALL_START"; input: Parameters<InvokeFn> }
  | { type: "CSB_API_CALL_SUCCESS"; data: AwaitedReturnType<InvokeFn> }
  | { type: "CSB_API_CALL_ERROR"; error: Error };

const reducer: Reducer<
  UseLegacyCsbApiState<UseCsbApiInvokeFn>,
  UseCsbApiAction<UseCsbApiInvokeFn>
> = (state, action) => {
  switch (action.type) {
    case "CSB_API_CALL_START": {
      return {
        state: "loading",
        error: null,
        input: action.input,
        data: null,
      };
    }

    case "CSB_API_CALL_SUCCESS": {
      return {
        state: "done",
        error: null,
        data: action.data,
        input: state.input as Parameters<UseCsbApiInvokeFn>,
      };
    }

    case "CSB_API_CALL_ERROR": {
      return {
        state: "done",
        error: action.error,
        data: null,
        input: state.input as Parameters<UseCsbApiInvokeFn>,
      };
    }

    default: {
      return state;
    }
  }
};

const initialState: UseLegacyCsbApiState<UseCsbApiInvokeFn> = {
  state: "idle",
  error: null,
  input: null,
  data: null,
};

type UseCsbApiCallback<InvokeFn extends UseCsbApiInvokeFn> = (
  csbApi: CsbApi,
) => InvokeFn;

type UseCsbApiInvokeFn = (
  // eslint-disable-next-line
  ...args: any[]
) => Promise<unknown>;

type UseCsbApiReturnType<InvokeFn extends UseCsbApiInvokeFn> = [
  state: UseLegacyCsbApiState<InvokeFn>,
  invoke: InvokeFn,
];

export function useLegacyCsbApi<InvokeFn extends UseCsbApiInvokeFn>(
  callback: UseCsbApiCallback<InvokeFn>,
): UseCsbApiReturnType<InvokeFn> {
  const { csbApi } = useEnvironmentInterface();
  const [state, dispatch] = useReducer(reducer, initialState);

  const action = useCallback<UseCsbApiInvokeFn>(
    (...args) => {
      if (csbApi == null) {
        return Promise.reject<Error>(new Error("CSB API is not initialized"));
      }

      const makeApiCall = callback(csbApi);

      dispatch({
        type: "CSB_API_CALL_START",
        input: args,
      });

      return makeApiCall(...args)
        .then((data) => {
          dispatch({
            type: "CSB_API_CALL_SUCCESS",
            data,
          });

          return data;
        })
        .catch((error) => {
          if (error instanceof Error) {
            dispatch({
              type: "CSB_API_CALL_ERROR",
              error,
            });
          }
        });
    },
    [callback],
  );

  return [state as UseLegacyCsbApiState<InvokeFn>, action as InvokeFn];
}
